import { env } from '@ria-client/env';
import { cn } from 'cn';
import { ChevronRight } from 'lucide-react';
import { Link } from '../layout/link';
import { BreadcrumbListLinkedData } from './BreadcrumbListLinkedData';
import type { BreadcrumbRoot } from './types';

export interface BreadcrumbsProps {
  breadcrumbs: BreadcrumbRoot[];
  /** Pages that emit their own BreadcrumbList JSON-LD (e.g. FA learn articles) pass false. */
  linkedData?: boolean;
}

// 10px/24px/0.3px: packages/ui/src/Theme/index.ts:1062-1068 breadcrumb variant.
const BREADCRUMB_TEXT = 'font-sans text-[10px] leading-6 tracking-[0.3px] font-bold uppercase';

function BreadcrumbLinks({ breadcrumbs, nwUrl }: BreadcrumbsProps & { nwUrl: string }) {
  return breadcrumbs.slice(0, -1).map(({ page: { title }, slug = '' }) => (
    <li className="flex items-center gap-2" key={title}>
      <ChevronRight aria-hidden="true" className="size-2 text-muted-foreground" />
      <Link
        href={
          new URL(
            slug,
            nwUrl,
          ).toString() /* nosemgrep: path-join-resolve-traversal — slug is a root-relative CMS path (e.g. /financial-advisors), never user input; new URL() is the Web URL API, not a filesystem operation */
        }
        title={`Back to ${title}`}
        aria-label={`Back to ${title}`}
        data-testid="crumb"
        className={cn(
          BREADCRUMB_TEXT,
          'whitespace-nowrap text-muted-foreground hover:text-primary hover:underline',
        )}
      >
        {title}
      </Link>
    </li>
  ));
}

export function Breadcrumbs({ breadcrumbs, linkedData = true }: BreadcrumbsProps) {
  const nwUrl = env.NEXT_PUBLIC_NW_URL;
  const lastBreadcrumb = breadcrumbs.at(-1);

  return (
    <>
      <nav aria-label="Breadcrumbs" className="flex w-full max-w-[1440px] items-center gap-2">
        <Link
          href={nwUrl}
          aria-label="Back to NerdWallet homepage"
          title="Back to NerdWallet homepage"
          className="inline-flex size-7 items-center justify-center rounded-full p-[5px] text-accent-foreground hover:bg-accent"
        >
          <svg aria-hidden="true" className="size-[18px] fill-current" viewBox="0 0 24 24">
            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
          </svg>
        </Link>
        <ol
          data-testid="breadcrumbs"
          className="m-0 hidden list-none items-center gap-2 p-0 lg:flex"
        >
          <BreadcrumbLinks breadcrumbs={breadcrumbs} nwUrl={nwUrl} />
          {lastBreadcrumb && (
            <li className="flex items-center gap-2">
              <ChevronRight aria-hidden="true" className="size-2 text-muted-foreground" />
              <span
                aria-current="page"
                data-testid="last-crumb"
                className={cn(
                  BREADCRUMB_TEXT,
                  'overflow-hidden text-ellipsis whitespace-nowrap text-muted-foreground',
                  breadcrumbs.length > 1 && 'max-w-[360px]',
                )}
              >
                {lastBreadcrumb.page.title}
              </span>
            </li>
          )}
        </ol>
        <ol
          data-testid="breadcrumbs-mobile"
          className="m-0 flex list-none items-center gap-2 p-0 lg:hidden"
        >
          <BreadcrumbLinks breadcrumbs={breadcrumbs} nwUrl={nwUrl} />
        </ol>
      </nav>
      {linkedData ? (
        <BreadcrumbListLinkedData id="breadcrumbs-linked-data" breadcrumbs={breadcrumbs} />
      ) : null}
    </>
  );
}
